import { useState, useEffect, useCallback } from 'react'
import {
  Loader2, Plus, Trash2, Star, ShieldCheck, AlertTriangle, Send, X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  fetchCompanyPayouts, fetchCompanyPayoutAccounts, createCompanyPayoutAccount,
  setDefaultCompanyPayoutAccount, deleteCompanyPayoutAccount, requestCompanyPayout,
  type CompanyPayoutAccount, type CompanyPayoutsResponse,
} from '../services/companyService'
import { money } from '../utils/money'
import { payoutNote } from '../utils/payoutNote'

/**
 * A company's own payouts.
 *
 * A collector who belongs to a company is paid through the company: the company
 * draws what its people earned and settles with them itself. This is where the
 * company adds an account and asks for what it is owed.
 */

const METHODS = [
  { value: 'MTN_MOMO', label: 'MTN MoMo' },
  { value: 'TELECEL_CASH', label: 'Telecel Cash' },
  { value: 'AIRTELTIGO_MONEY', label: 'AirtelTigo Money' },
  // No "Bank transfer": the server needs a bank code and there is no bank
  // picker yet, so every bank account was refused. Same as the collector app.
]

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PROCESSING: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  FAILED: 'bg-red-100 text-red-700',
}

const EMPTY_ACCOUNT = { method: 'MTN_MOMO', accountName: '', accountNumber: '', bankName: '' }


export default function CompanyPayouts({ suspended = false }: { suspended?: boolean }) {
  const [data, setData] = useState<CompanyPayoutsResponse | null>(null)
  const [accounts, setAccounts] = useState<CompanyPayoutAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT)
  const [accountFormOpen, setAccountFormOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [selectedAccount, setSelectedAccount] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const payouts = await fetchCompanyPayouts()
      setData(payouts)
      const { accounts: list } = await fetchCompanyPayoutAccounts()
      setAccounts(list)
      setSelectedAccount(list.find((a) => a.isDefault)?.id ?? list[0]?.id ?? '')
    } catch {
      toast.error('Failed to load payouts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleAddAccount = async () => {
    if (!accountForm.accountName.trim() || !accountForm.accountNumber.trim()) {
      toast.error('Account name and number are required')
      return
    }
    setBusy(true)
    try {
      await createCompanyPayoutAccount({
        method: accountForm.method,
        accountName: accountForm.accountName.trim(),
        accountNumber: accountForm.accountNumber.trim(),
        bankName: accountForm.bankName.trim() || undefined,
      })
      toast.success('Payout account added')
      setAccountForm(EMPTY_ACCOUNT)
      setAccountFormOpen(false)
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to add the account')
    } finally {
      setBusy(false)
    }
  }

  const handleRequest = async () => {
    const value = Number(amount)
    if (!Number.isFinite(value) || value < 1) {
      toast.error('Enter an amount of at least GHS 1.00')
      return
    }
    if (!selectedAccount) {
      toast.error('Choose an account to be paid into')
      return
    }
    setBusy(true)
    try {
      await requestCompanyPayout(value, selectedAccount)
      toast.success('Payout requested. SmartWasteCycle will review it.')
      setAmount('')
      setRequestOpen(false)
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to request the payout')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!data) return null

  const { balance, currency } = data

  return (
    <div className="space-y-5">
      {suspended && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-800">Your company is suspended. Balances are shown, but accounts and payout requests cannot be changed until SmartWasteCycle lifts the suspension.</p>
        </div>
      )}
      {/* Balance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Available to request', value: money(balance.availableToCompany, currency), color: 'text-green-700 bg-green-50' },
          { label: 'Gross earned', value: money(balance.grossEarned, currency), color: 'text-blue-600 bg-blue-50' },
          { label: 'Paid to you', value: money(balance.paidToCompany, currency), color: 'text-purple-600 bg-purple-50' },
          { label: 'Awaiting settlement', value: money(balance.committedToCompany, currency), color: 'text-orange-600 bg-orange-50' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-[#6B7280] font-medium">{label}</p>
            <p className={`text-xl font-bold mt-1 ${color.split(' ')[0]}`}>{value}</p>
          </div>
        ))}
      </div>

      {(balance.paidToCollectors > 0 || balance.committedToCollectors > 0) && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800">
            {money(balance.paidToCollectors + balance.committedToCollectors, currency)} of your
            collectors' earnings was paid to them individually for work done before they joined you.
            That amount is not available to request again.
          </p>
        </div>
      )}

      {/* Accounts */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-[#1A1A2E]">Where you get paid</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAccountFormOpen((open) => !open)}
              disabled={suspended}
              title={suspended ? 'Your company is suspended' : undefined}
              className="flex items-center gap-1.5 text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 px-3 py-1.5 rounded-xl hover:bg-primary-100 transition disabled:opacity-40"
            >
              {accountFormOpen ? <X size={12} /> : <Plus size={12} />}
              {accountFormOpen ? 'Cancel' : 'Add account'}
            </button>
            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              disabled={suspended || accounts.length === 0 || balance.availableToCompany <= 0}
              title={suspended ? 'Your company is suspended' : undefined}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-3 py-1.5 rounded-xl hover:bg-primary-700 transition disabled:opacity-40"
            >
              <Send size={12} />
              Request payout
            </button>
          </div>
        </div>

        {accounts.length === 0 ? (
          <p className="text-sm text-[#9CA3AF] italic">
            Add an account before requesting a payout.
          </p>
        ) : (
          <div className="space-y-2">
            {accounts.map((account) => (
              <div key={account.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1A1A2E] truncate flex items-center gap-1.5">
                    {account.accountName}
                    {account.isDefault && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700">
                        <Star size={8} /> Default
                      </span>
                    )}
                    {account.isVerified && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                        <ShieldCheck size={8} /> Verified
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-[#6B7280]">
                    {METHODS.find((m) => m.value === account.method)?.label ?? account.method}
                    {' · '}
                    {account.accountNumber}
                    {account.bankName ? ` · ${account.bankName}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!account.isDefault && (
                    <button
                      type="button"
                      disabled={suspended}
                      onClick={async () => {
                        try {
                          await setDefaultCompanyPayoutAccount(account.id)
                          load()
                        } catch (err) {
                          const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                          toast.error(message || 'Could not change the default account')
                        }
                      }}
                      className="text-[11px] font-semibold text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-lg transition"
                    >
                      Make default
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={suspended}
                    onClick={async () => {
                      try {
                        await deleteCompanyPayoutAccount(account.id)
                        toast.success('Account removed')
                        load()
                      } catch (err) {
                        const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                        toast.error(message || 'Failed to remove the account')
                      }
                    }}
                    className="text-red-600 hover:bg-red-50 p-1 rounded-lg transition"
                    aria-label={`Remove ${account.accountName}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {accountFormOpen && (
          <div className="mt-3 border border-gray-100 rounded-xl p-3 space-y-2 bg-[#F8FAF9]">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-medium text-[#6B7280] mb-1" id="payout-method">Method</label>
                <select
                  value={accountForm.method}
                  onChange={(e) => setAccountForm((f) => ({ ...f, method: e.target.value }))}
                  aria-labelledby="payout-method"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <input
                value={accountForm.accountName}
                onChange={(e) => setAccountForm((f) => ({ ...f, accountName: e.target.value }))}
                placeholder="Account name"
                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs self-end focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                value={accountForm.accountNumber}
                onChange={(e) => setAccountForm((f) => ({ ...f, accountNumber: e.target.value }))}
                placeholder="Account or wallet number"
                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <input
                value={accountForm.bankName}
                onChange={(e) => setAccountForm((f) => ({ ...f, bankName: e.target.value }))}
                placeholder="Bank name (optional)"
                className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={handleAddAccount}
              disabled={busy}
              className="w-full bg-primary-600 text-white text-xs font-semibold py-2 rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
            >
              {busy ? 'Adding…' : 'Add account'}
            </button>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <p className="text-sm font-bold text-[#1A1A2E] p-5 pb-3">Payout history</p>
        {data.requests.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-[#9CA3AF] italic">No payouts requested yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Requested', 'Amount', 'Account', 'Status', 'Note'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.requests.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-[#1A1A2E]">
                      {money(r.amount, r.currency)}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280]">{r.account?.accountName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {r.status.charAt(0) + r.status.slice(1).toLowerCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[220px] truncate">
                      {payoutNote(r) ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Request modal */}
      {requestOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-[#1A1A2E] mb-1">Request a payout</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Up to {money(balance.availableToCompany, currency)} is available.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Amount ({currency})</label>
                <input
                  type="number"
                  min={1}
                  max={balance.availableToCompany}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1" id="payout-account">Pay into</label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  aria-labelledby="payout-account"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.accountName} · {a.accountNumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => setRequestOpen(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRequest}
                disabled={busy}
                className="flex-1 py-2.5 bg-primary-600 rounded-xl text-sm font-semibold text-white hover:bg-primary-700 transition disabled:opacity-50"
              >
                {busy ? 'Requesting…' : 'Request payout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
